import { Card, Flex } from '@mantine/core';
import { UseMutationResult } from '@tanstack/react-query';
import colors from 'common/constants/colors.styles';
import { string2money } from 'common/utils/string';
import { useFormState } from 'components/elements/form';
import Text from 'mantine-components/widget/text';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import UniskaiButton, {
  UniskaiButtonProps,
} from 'templates/actions/uniskai-button';

export interface UniskaiFormFooterProps<T = any> {
  onClickDetail?: () => void;
  data?: T;
  saveMutation?: UseMutationResult;
  cancelProps?: Omit<UniskaiButtonProps, 'type'>;
  saveProps?: Omit<UniskaiButtonProps, 'type'>;
}

export type UniskaiFormFooterRef = {
  total: number;
  setTotal: React.Dispatch<React.SetStateAction<number>>;
};

const UniskaiFormFooter = React.forwardRef<
  UniskaiFormFooterRef,
  UniskaiFormFooterProps
>((props, ref) => {
  const { editable, setIsEditable } = useFormState();
  const { reset } = useFormContext();
  const { t } = useTranslation();

  const { onClickDetail, data, saveMutation, cancelProps, saveProps } = props;

  const isSubmitting =
    typeof saveMutation?.isLoading === 'boolean'
      ? saveMutation.isLoading === true
      : false;

  const [total, setTotal] = React.useState(0);

  React.useImperativeHandle(ref, () => {
    return {
      total,
      setTotal,
    };
  });

  return (
    <Card withBorder shadow="md">
      <Flex
        direction={{
          md: 'row',
          base: 'column',
        }}
        gap={16}
        bg={colors.white}
      >
        <Flex direction="row" gap={8} align="center" flex={1}>
          <Text variant="paragraphSmall">{t('common:total')}</Text>
          <Text variant="body1Bold">{string2money(total || 0)} IDR</Text>
          {onClickDetail && (
            <UniskaiButton
              type="action"
              isLite
              variant="subtle"
              onClick={onClickDetail}
            />
          )}
        </Flex>
        {editable && (
          <Flex gap={16} direction="row" align="center">
            {!isSubmitting && !!data && (
              <UniskaiButton
                {...cancelProps}
                flex={1}
                miw={100}
                isResponsive={false}
                type="cancel"
                onClick={() => {
                  setIsEditable(false);
                  reset();
                }}
              />
            )}
            <UniskaiButton
              {...saveProps}
              flex={1}
              miw={100}
              isResponsive={false}
              type="save"
              loading={saveMutation?.isLoading}
            />
          </Flex>
        )}
      </Flex>
    </Card>
  );
});

UniskaiFormFooter.displayName = 'UniskaiFormFooter';

export default UniskaiFormFooter;
